import React from 'react';

import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Collapse, Dialog, DialogActions, DialogProps, DialogTitle } from '@mui/material';
import { get, isEmpty, isNil, noop, sumBy } from 'lodash';
import { joinPairs } from '../../../../utils/libs';
import { createScopedI18n } from '../../../../i18n/i18n';
import { LoadingButton } from '../../../../components';

const jobDetailPageI18n = createScopedI18n('pages.jobs_id');

const getPeriodJobCancelFeeGql = gql`
  query getPeriodJobCancelFee($periodJobId: ID) {
    periodJobs(id: $periodJobId) {
      id
      jobs {
        id
        cancelFee
        scheduleDate
      }
    }
  }
`;

const cancelPeriodJobGql = gql(`
  mutation cancelPeriodJob($periodJobId: ID!) {
    cancelPeriodJob(periodJobId: $periodJobId) {
      success
      errors
    }
  }
`);

export type CancelPeriodJobDialogProps = {
  periodJobId: string;
  onSuccess?: () => void;
} & DialogProps;

const CancelPeriodJobDialog = ({
  periodJobId,
  open,
  onClose,
  onSuccess,
  ...dialogProps
}: CancelPeriodJobDialogProps) => {
  const { data: cancelPeriodJobFeeData, loading: cancelPeriodJobFeeLoading } = useQuery(getPeriodJobCancelFeeGql, {
    variables: { periodJobId },
    fetchPolicy: 'network-only',
    skip: !open || isNil(periodJobId),
  });

  const jobs = React.useMemo(() => cancelPeriodJobFeeData?.periodJobs?.[0].jobs, [cancelPeriodJobFeeData?.periodJobs]);
  const sumCancelFee = React.useMemo(() => (isEmpty(jobs) ? 0 : sumBy(jobs, 'cancelFee')), [jobs]);

  const [cancelPeriodJobMutation, { loading: cancelPeriodJobMutationLoading }] = useMutation(cancelPeriodJobGql);

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const submitHandler = async () => {
    if (isNil(periodJobId)) return;

    setErrorMessage(null);
    try {
      const { data } = await cancelPeriodJobMutation({ variables: { periodJobId } });

      if (data?.cancelPeriodJob?.success) {
        onClose?.({}, 'backdropClick');
        onSuccess?.();
      } else {
        const errors = data?.cancelPeriodJob?.errors;
        setErrorMessage(joinPairs(errors));
      }
    } catch (error) {
      setErrorMessage(get(error, 'message', null));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} {...dialogProps}>
      <DialogTitle>{jobDetailPageI18n('cancel_job_title', { count: sumCancelFee || 0 })}</DialogTitle>

      <Collapse in={!isEmpty(errorMessage)} sx={{ width: '100%', paddingX: 2 }}>
        <Alert severity="error">{errorMessage}</Alert>
      </Collapse>

      <DialogActions>
        <LoadingButton
          color="error"
          loading={cancelPeriodJobMutationLoading}
          disabled={cancelPeriodJobFeeLoading || cancelPeriodJobMutationLoading}
          onClick={submitHandler}
        >
          {jobDetailPageI18n('cancel_job_fee', { count: sumCancelFee || 0 })}
        </LoadingButton>
        <Button variant="contained" disableElevation onClick={onClose ?? noop}>
          {jobDetailPageI18n('continue_job')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelPeriodJobDialog;
